/* eslint-disable max-params */
import type { ChatAppResponse, ChatMessageModel, DeepStateResponse } from "../models"

import { logger } from "@/lib/logger"

import { getManagedCredential } from "../auth-refresh-coordinator"
import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/**
 * QUERY: Gets all messages for a session
 *
 * Thread hydration (`useSessionMessages`) passes the legacy settings token,
 * which is unset in the hosted-agent workspace: the first attempt went out
 * with no bearer on every thread open ("No authentication headers found").
 * The managed credential rides the first attempt instead, as it does for the
 * other hosted-agent calls.
 *
 * @param sessionId - The session ID
 * @param idToken - Optional authentication token
 * @returns Array of chat messages
 * @category Query
 */
export async function getMessagesApi(
  sessionId: string,
  idToken?: string,
  limit: number = 100
): Promise<ChatMessageModel[]> {
  const bearer = idToken ?? getManagedCredential()
  try {
    const response = await fetchWithAuthRetry(
      `${BACKEND_URI}/get-messages?session_id=${sessionId}&limit=${limit}`,
      {
        method: "GET",
        headers: getHeaders(bearer),
      },
      bearer
    )

    const messages: ChatMessageModel[] = await response.json()
    return messages
  } catch (error) {
    logger.error("Error fetching messages", error as Error)
    return []
  }
}

const EMPTY_DEEP_STATE: DeepStateResponse = {
  had_state: false,
  todos: [],
  files: {},
  file_prev_content: {},
  snapshot_seq: 0,
  token_budget: null,
  live_activity_events: [],
  live_data_points: {},
  live_documents: [],
  live_artifacts: [],
  live_updated_at: null,
  broadcaster_active: false,
  plan_approved: false,
}

/**
 * QUERY: Fetches the persisted Deep Explorer agent state for a session.
 *
 * Used on session open to rehydrate the Deep side panel (todos, files,
 * snapshot_seq, token_budget). Returns an empty state on any failure or when
 * the checkpointer has no record — hydration must never throw into the
 * session-load path.
 * @category Query
 */
export async function getDeepStateApi(
  sessionId: string,
  idToken?: string
): Promise<DeepStateResponse> {
  try {
    const response = await fetchWithAuthRetry(
      `${BACKEND_URI}/explorer-deep/state?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: "GET",
        headers: getHeaders(idToken),
      },
      idToken
    )
    if (!response.ok) {
      return EMPTY_DEEP_STATE
    }
    return (await response.json()) as DeepStateResponse
  } catch (error) {
    logger.error("Error fetching deep state", error as Error)
    return EMPTY_DEEP_STATE
  }
}

// ── Agent runs ────────────────────────────────────────────────────────────
// Backend-owned registry of deep-explorer runs the user has launched.
// Replaces the old localStorage-only `useResearchRunsStore` bookmark.

export interface AgentRunModel {
  session_id: string
  title: string
  session_type: string
  status: "executing" | "synthesizing" | "done" | "failed" | "cancelled"
  started_at: string | null
  ended_at: string | null
  unread: boolean
  created: string | null
}

/**
 * The hosted-agent workspace marks runs read without a token of its own; the
 * managed bearer rides the FIRST attempt so it is not a 401 -> refresh ->
 * replay round trip on every thread switch (#3207).
 */
function agentRunHeaders(idToken?: string): Record<string, string> {
  return getHeaders(idToken ?? getManagedCredential())
}

export async function listAgentRunsApi(idToken?: string): Promise<AgentRunModel[]> {
  try {
    const response = await fetchWithAuthRetry(
      `${BACKEND_URI}/agent-runs`,
      { method: "GET", headers: agentRunHeaders(idToken) },
      idToken
    )
    if (!response.ok) return []
    const body = (await response.json()) as { runs?: AgentRunModel[] }
    return body.runs ?? []
  } catch (error) {
    logger.error("Error fetching agent runs", error as Error)
    return []
  }
}

export async function markAgentRunReadApi(sessionId: string, idToken?: string): Promise<void> {
  await fetchWithAuthRetry(
    `${BACKEND_URI}/agent-runs/${encodeURIComponent(sessionId)}/read`,
    { method: "POST", headers: agentRunHeaders(idToken) },
    idToken
  )
}

export async function dismissAgentRunApi(sessionId: string, idToken?: string): Promise<void> {
  await fetchWithAuthRetry(
    `${BACKEND_URI}/agent-runs/${encodeURIComponent(sessionId)}`,
    { method: "DELETE", headers: agentRunHeaders(idToken) },
    idToken
  )
}

export async function cancelAgentRunApi(sessionId: string, idToken?: string): Promise<void> {
  await fetchWithAuthRetry(
    `${BACKEND_URI}/agent-runs/${encodeURIComponent(sessionId)}/cancel`,
    { method: "POST", headers: agentRunHeaders(idToken) },
    idToken
  )
}

/**
 * MUTATION: Updates a chat message
 * @param messageId - The message ID to update
 * @param content - New message content
 * @param idToken - Optional authentication token
 * @param editDescription - Optional description of the edit
 * @param useVersions - Whether to use versioning
 * @param deletedVersions - Array of version numbers to delete
 * @returns Success status and message ID
 * @category Mutation
 */
export async function updateMessageApi(
  messageId: string,
  content: string,
  idToken?: string,
  editDescription?: string,
  useVersions: boolean = true,
  deletedVersions?: number[]
): Promise<{ success: boolean; message_id: string }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/update-message`,
    {
      method: "PUT",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        message_id: messageId,
        content,
        edit_description: editDescription,
        use_versions: useVersions,
        deleted_versions: deletedVersions,
      }),
    },
    idToken
  )

  return await response.json()
}

export async function saveWorkflowRunMessageApi(
  sessionId: string,
  question: string,
  responseMessage: ChatAppResponse,
  idToken?: string
): Promise<{ success: boolean; message_id: string }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/sessions/${encodeURIComponent(sessionId)}/workflow-run-message`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        question,
        response: responseMessage,
        message_id: responseMessage.messageId,
      }),
    },
    idToken
  )

  return await response.json()
}
